/** @format */

import {
  BOPIS_FILTER,
  DEFAULT_PAGE_SIZE,
  FACET_STORE,
  FILTER_ID_5004,
  SALE_FILTER,
} from '@plp/data-access/constants';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import {
  PersonalizedPCS,
  PersonalizedPinnedContent,
  PinnedContent,
  PzPlpBannerItem,
} from '@plp/data-access/interfaces';

import { HeaderStoreSelection } from '@shared/data-access/interfaces';
import { PzPinnedContentType } from '@plp/data-access/enums';
import { WINDOW } from '@shared/data-access/services';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class ProductListUIService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly window = inject(WINDOW);

  /**
   * @description Filters the pinned content based on the active filters.
   */
  filterPersonalizedPinnedContent(
    pinnedContent: PinnedContent | PersonalizedPinnedContent,
    filters: Record<string, string[]>,
    selectedStoreInfo: HeaderStoreSelection | null,
  ): PinnedContent | PersonalizedPinnedContent {
    const filterState = {
      isSaleFilterActive: filters[FILTER_ID_5004]?.includes(SALE_FILTER) ?? false,
      isBopisFilterActive: filters[FACET_STORE]?.includes(BOPIS_FILTER) ?? false,
    };

    let filtered = pinnedContent;

    if (filterState.isSaleFilterActive) {
      filtered = this.excludeByPcsType(filtered, PzPinnedContentType.DEAL_SEEKERS);
    }

    if (filterState.isBopisFilterActive || !selectedStoreInfo) {
      filtered = this.excludeByPcsType(filtered, PzPinnedContentType.BOPIS);
    }

    return filtered;
  }

  /**
   * @description Filters PLP banners based on active filters and store selection.
   * - standard: always shown
   * - bopis: excluded when isBopisFilterActive OR no store selected
   * - dealSeekers: excluded when isSaleFilterActive
   */
  public filterPersonalizedBanners(
    banners: PzPlpBannerItem[],
    filters: Record<string, string[]>,
    selectedStoreInfo: HeaderStoreSelection | null,
  ): PzPlpBannerItem[] {
    const isSaleFilterActive = filters[FILTER_ID_5004]?.includes(SALE_FILTER) ?? false;
    const isBopisFilterActive = filters[FACET_STORE]?.includes(BOPIS_FILTER) ?? false;

    return banners.filter(
      (banner) =>
        !(banner.bannerType === 'dealSeekers' && isSaleFilterActive) &&
        !(banner.bannerType === 'bopis' && (isBopisFilterActive || !selectedStoreInfo)),
    );
  }

  /**
   * @description Excludes pinned content data of a specific PCS type from the given pinnedContent object.
   */
  private excludeByPcsType(
    pinnedContent: PinnedContent | PersonalizedPinnedContent,
    excludeType: PzPinnedContentType,
  ): PinnedContent | PersonalizedPinnedContent {
    return Object.entries(pinnedContent).reduce(
      (result, [key, value]) => {
        if ((value as PersonalizedPCS)?.pcsType !== excludeType) {
          result[Number(key)] = value;
        }
        return result;
      },
      {} as PinnedContent | PersonalizedPinnedContent,
    );
  }

  /**
   * @description Checks if any pinned content item has isPersonalizedContent set to true.
   */
  hasPersonalizedContent(pinnedContent: PinnedContent | PersonalizedPinnedContent | null): boolean {
    if (!pinnedContent) {
      return false;
    }

    return Object.values(pinnedContent).some(
      (value) => (value as PersonalizedPCS)?.isPersonalizedContent,
    );
  }

  /**
   * @description Function is used to format pricing data to be displayed for the pricing checkboxes.
   */
  addSymbolForPriceDisplay(value: string): string {
    let valArray: string[] = [];
    let displayText = '';
    if (value) {
      valArray = value.split('-').map((x) => this.formatNumberWithComma(x));
    }
    displayText = `$${valArray[0]} - $${valArray[1]}`;
    if (valArray[0] === '0' || valArray[0] === '*') {
      displayText = `Under $${valArray[1]}`;
    }
    if (valArray[1] === '*') {
      displayText = `$${valArray[0]} & Above`;
    }
    return displayText;
  }

  private formatNumberWithComma(rawValue: string | undefined): string {
    const part = rawValue?.trim() ?? '';
    if (!part || part === '*') {
      return part;
    }

    const num = Number(part);
    return Number.isFinite(num) ? num.toLocaleString('en-US', { maximumFractionDigits: 0 }) : part;
  }

  /**
   * @description Returns the pageSize depending whether there is an assigned value, otherwise returns the default value.
   */
  getPageSize(pageSize: string | null | undefined): number {
    return pageSize ? Number.parseInt(pageSize, 10) : DEFAULT_PAGE_SIZE;
  }

  /**
   * @description Returns the pageNumber depending whether there is an assigned value, otherwise returns the default value.
   */
  getPageNumber(pageNumber: string | null | undefined): number {
    return pageNumber ? Number.parseInt(pageNumber, 10) : 0;
  }

  redirectToPLPOrPDP(landingPageURL: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const url = landingPageURL.trim();
    const isExternalLink = /^(https?:\/\/|www\.)/i.test(url);
    if (isExternalLink) {
      const hasProtocol = /^(https?:\/\/)/i.test(url);
      const finalUrl = hasProtocol ? url : `https://${url}`;
      this.window.location.href = finalUrl;
    } else if (url.startsWith('/')) {
      this.window.location.href = `${this.window.location.origin}${url}`;
    } else {
      this.window.location.href = url;
    }
  }
}
